import React, { useEffect, useState, useRef } from 'react';
import Header from '../components/common/Header';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { api } from '../services/api';

const PROGRESS_MESSAGES = [
    "Initializing GEO footprint analysis...",
    "Fetching remote crawler accessibility...",
    "Evaluating AI language model 'citability'...",
    "Cross-referencing global brand mentions...",
    "Generating structured technical schema...",
    "Optimizing for ChatGPT, Perplexity, and Google AIO...",
    "Synthesizing high-impact conversion directives...",
    "Drafting executive PDF deliverable..."
];

const TerminalView: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [logs, setLogs] = useState<string[]>(['> Initializing secure connection to backend execution framework...', '> Allocating computing resources...']);
    const [status, setStatus] = useState<string>('Booting up connection...');
    const [downloadLink, setDownloadLink] = useState<string | null>(null);
    const terminalEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        terminalEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [logs]);

    useEffect(() => {
        const userId = (user as any)?._id || (user as any)?.id;
        console.log("[TerminalView] Mounted React Component. Hydrated User ID:", userId);

        if (!userId) {
            console.log("[TerminalView] Waiting for valid User ID...");
            return;
        }

        const sid = searchParams.get('session_id');
        let socket: any;

        const initTerminal = async () => {
            if (sid) {
                setLogs(prev => [...prev, `> [Diagnostics] Extracted Stripe Session ID: ${sid.substring(0, 14)}...`]);

                try {
                    setLogs(prev => [...prev, '> [Diagnostics] Executing manual Sync ping to backend API...']);
                    await api.post<any>('/api/audits/sync', { session_id: sid });
                    setLogs(prev => [...prev, '> [Diagnostics] Sync ping completed. Retrieving formal database state.']);
                } catch (e) {
                    setLogs(prev => [...prev, '> [Diagnostics] Manual Sync ping errored.']);
                    console.error("Manual session sync failed natively.", e);
                }

                try {
                    const res = await api.get<any>('/api/audits');
                    if (res && res.success) {
                        const auditsArray = (res as any).audits || (res.data && res.data.audits) || res.data || [];
                        const matched = auditsArray.find((a: any) => a.stripeSessionId === sid);

                        if (matched) {
                            setLogs(prev => [...prev, `> [Diagnostics] Audit securely located in DB. Core Status: '${matched.status}'`]);

                            if (matched.logs && matched.logs.length > 0) {
                                setLogs(matched.logs);
                            }

                            if (matched.status === 'completed') {
                                setStatus('Audit Complete');
                                setLogs(prev => [...prev, '> Remote Audit explicitly concluded in database.', '> Asset Pipeline safely vaulted PDF deliverables into localized encryption index.']);
                                setDownloadLink(matched.s3Link && matched.s3Link.startsWith('http') ? matched.s3Link : `/api/audits/${matched._id}/download`);
                                return; // Short-circuit connection
                            } else if (matched.status === 'failed') {
                                setStatus('Audit Failed');
                                setLogs(prev => [...prev, '> ❌ CRITICAL: The backend execution engine permanently halted.', '> Your checkout session has been fully refunded.']);
                                return; // Short-circuit connection
                            } else if (matched.status === 'running') {
                                setStatus('Audit Running');
                            } else if (matched.status === 'pending') {
                                setLogs(prev => [
                                    ...prev,
                                    '> [Diagnostics] Target session is fundamentally logged as PENDING.',
                                    '> [Diagnostics] Your Stripe payment intentionally did not clear, OR the execution sandbox is actively stalling.',
                                    '> [Diagnostics] Attempting fallback Socket stream connection...'
                                ]);
                            }
                        } else {
                            setLogs(prev => [...prev, '> [Diagnostics] ❌ WARNING: No matching audit found in your DB payload for this Stripe ID!']);
                        }
                    }
                } catch (e) {
                    console.error("Failed to hydrate prior Terminal state natively:", e);
                }
            } else {
                setLogs(prev => [...prev, `> [Diagnostics] NO SESSION ID PROVIDED IN URL. Cannot hydrate state. Operating in blind Socket mode.`]);
            }

            console.log(`[TerminalView] Attempting explicitly bound Socket.IO handshake to ${window.location.origin}...`);
            setLogs(prev => [...prev, `> [Diagnostics] Formalizing secure socket layer to intercept the AI telemetry stream...`]);

            socket = io(window.location.origin, { auth: { userId } });

            socket.on('connect_error', (err: any) => { setStatus(`Connection Error: ${err.message}`); });
            socket.on('disconnect', () => { });

            socket.on('connect', () => {
                setStatus('Connected');
                setLogs(prev => [...prev, '> Socket Connected to Backend Worker.', '> Waiting for Stripe Webhook to complete analysis routing...']);
            });

            socket.on('audit_starting', (data: any) => {
                setStatus('Audit Running');
                setLogs(prev => [...prev, `> Launched GEO Audit for ${data.targetUrl}`, '>']);
            });

            socket.on('audit_log', (data: any) => { setLogs(prev => [...prev, data.text]); });

            socket.on('audit_complete', (data: any) => {
                setStatus('Audit Complete');
                setLogs(prev => [...prev, '> ' + data.message]);
                if (data.success && data.downloadLink) setDownloadLink(data.downloadLink);
            });
        };

        initTerminal();

        return () => {
            if (socket) socket.disconnect();
        };
    }, [user, searchParams]);

    const handleDownload = async () => {
        if (!downloadLink) return;
        if (downloadLink.startsWith('http')) {
            window.open(downloadLink, '_blank');
            navigate('/dashboard');
            return;
        }

        try {
            const resp = await api.get<any>(downloadLink);
            if (resp.success && resp.data?.url) {
                window.open(resp.data.url, '_blank');
                navigate('/dashboard');
            } else {
                console.error("Secure download sequence rejected natively:", resp.error);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [progressMessageIndex, setProgressMessageIndex] = useState(0);

    useEffect(() => {
        if (status !== 'Audit Running') return;
        const timer = setInterval(() => {
            setElapsedSeconds(prev => prev + 1);
        }, 1000);

        const messageTimer = setInterval(() => {
            setProgressMessageIndex(prev => (prev + 1) % PROGRESS_MESSAGES.length);
        }, 8000);

        return () => {
            clearInterval(timer);
            clearInterval(messageTimer);
        };
    }, [status]);

    const estimatedTotalSeconds = 300;
    const progressPercent = status === 'Audit Complete' ? 100 : Math.min(95, Math.floor((elapsedSeconds / estimatedTotalSeconds) * 100));

    return (
        <div style={{ background: '#111', color: '#4ade80', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <style>{`
                @keyframes dots {
                    0% { content: ''; }
                    25% { content: '.'; }
                    50% { content: '..'; }
                    75% { content: '...'; }
                    100% { content: ''; }
                }
                .terminal-dots::after {
                    content: '';
                    animation: dots 1.5s infinite;
                }
                @keyframes pulse {
                    0% { transform: translateX(-100%); opacity: 0; }
                    50% { opacity: 0.3; }
                    100% { transform: translateX(100%); opacity: 0; }
                }
            `}</style>
            <Header />
            <div style={{ padding: '2rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', borderBottom: '1px solid #333', paddingBottom: '1rem' }}>
                    <h2 style={{ margin: 0, fontFamily: '"Share Tech Mono", Courier, monospace' }}>GEO Terminal Instance</h2>
                    <span style={{ color: status === 'Audit Running' ? '#fbbf24' : '#888', fontFamily: '"Share Tech Mono", Courier, monospace', fontWeight: 'bold' }}>{status}</span>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', fontFamily: '"Share Tech Mono", Courier, monospace', fontSize: '14px', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                    {logs.map((log, i) => (
                        <div key={i} style={{ opacity: log.startsWith('> ') ? 0.7 : 1, color: log.includes('[ERROR]') ? '#ef4444' : '#4ade80' }}>{log}</div>
                    ))}

                    {status === 'Audit Running' && (
                        <div style={{ opacity: 0.7, color: '#4ade80', marginTop: '4px' }}>
                            &gt; <span className="terminal-dots"></span>
                        </div>
                    )}

                    {(status === 'Audit Running' || status === 'Audit Complete') && (
                        <div style={{ marginTop: '2rem', background: '#1a1a1a', padding: '1.5rem', borderRadius: '8px', border: '1px solid #333' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', fontFamily: '"Share Tech Mono", Courier, monospace' }}>
                                <div style={{ color: status === 'Audit Complete' ? '#4ade80' : '#fbbf24', fontWeight: 'bold' }}>
                                    {status === 'Audit Complete' ? '✅ Analysis Complete' : `⏳ Analysis In Progress...`}
                                </div>
                                <div style={{ color: '#888' }}>
                                    {Math.floor(elapsedSeconds / 60)}:{(elapsedSeconds % 60).toString().padStart(2, '0')} Elapsed
                                </div>
                            </div>

                            <div style={{ width: '100%', background: '#333', borderRadius: '4px', height: '12px', overflow: 'hidden', marginBottom: '1rem' }}>
                                <div style={{
                                    width: `${progressPercent}%`,
                                    background: status === 'Audit Complete' ? '#4ade80' : 'linear-gradient(90deg, #fbbf24, #f59e0b)',
                                    height: '100%',
                                    transition: 'width 1s ease-in-out, background 0.5s',
                                    position: 'relative'
                                }}>
                                    {status === 'Audit Running' && (
                                        <div style={{
                                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                                            animation: 'pulse 2s infinite'
                                        }} />
                                    )}
                                </div>
                            </div>

                            {status === 'Audit Running' && (
                                <div style={{ color: '#4ade80', fontSize: '0.9rem', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: '"Share Tech Mono", Courier, monospace' }}>
                                    <span style={{ display: 'inline-block' }}>⚙️</span>
                                    {PROGRESS_MESSAGES[progressMessageIndex]}
                                </div>
                            )}
                        </div>
                    )}

                    <div ref={terminalEndRef} />
                </div>

                {downloadLink && (
                    <div style={{ marginTop: '2rem', textAlign: 'center', background: '#111', padding: '1.5rem', borderRadius: '8px' }}>
                        <h3 style={{ color: '#fff', marginBottom: '1rem' }}>✅ Optimization Directives Generated</h3>
                        <p style={{ color: '#888', marginBottom: '1.5rem' }}>Your GEO Audit is fully complete. Download your developer implementation guide below.</p>
                        <button onClick={handleDownload} style={{ cursor: 'pointer', border: 'none', background: '#4ade80', color: '#000', padding: '1rem 2rem', textDecoration: 'none', fontWeight: 'bold', borderRadius: '4px', display: 'inline-block', width: 'fit-content', maxWidth: '60%' }}>
                            Download Premium PDF Deliverable
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TerminalView;
